import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/ThemeContext'
import { useLocale } from '../../contexts/LocaleContext'
import type { GroupCell, GroupKPIMeta, GroupMatrixRow, GroupTrend, Tier } from '../../api/groupSteering'
import { TIER_VAR, formatUnit, langOf, text, tierOf, token } from './format'

/**
 * The charts of the console. One axis per chart, always; the pooled value,
 * the peers' median and the target on the trend; bars sorted by distance to
 * the target, tinted by tier, n under each; a scatter of value × volume.
 * Colours are theme tokens resolved at render time through `token()`, so a
 * theme switch re-renders them (the hook reads the theme for that alone).
 */

function useChartTokens() {
    const { theme } = useTheme()
    return useMemo(
        () => ({
            accent: token('--chart-1', '#2563eb'),
            excellent: token('--chart-2', '#16a34a'),
            watch: token('--destructive', '#dc2626'),
            muted: token('--muted-foreground', '#6b7280'),
            border: token('--border', '#e5e7eb'),
            fg: token('--foreground', '#111827'),
            _theme: theme,
        }),
        [theme],
    )
}

function tierColor(tier: Tier, tokens: ReturnType<typeof useChartTokens>): string {
    switch (tier) {
        case 'excellent':
            return tokens.excellent
        case 'watch':
            return tokens.watch
        case 'healthy':
            return tokens.accent
        default:
            return tokens.muted
    }
}

const axisFormatter = (unit: string, lang: 'en' | 'fr', locale: string) => (v: number) => formatUnit(v, unit, lang, locale)

/** The pooled series, the peers' median (and band), the target, the out-of-target perimeters in grey. */
export function TrendChart({ trend, kpi }: { trend: GroupTrend; kpi: GroupKPIMeta }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const tokens = useChartTokens()
    const months = trend.months.map((m) => m.month)
    const fmt = axisFormatter(kpi.unit, lang, locale)
    const option = useMemo(() => {
        const series: Record<string, unknown>[] = []
        const hasBand = trend.months.some((m) => m.peers_p10 !== undefined && m.peers_p90 !== undefined)
        if (hasBand) {
            series.push(
                { name: 'p10', type: 'line', data: trend.months.map((m) => m.peers_p10 ?? null), lineStyle: { opacity: 0 }, stack: 'band', symbol: 'none', silent: true },
                {
                    name: 'p10–p90',
                    type: 'line',
                    data: trend.months.map((m) => (m.peers_p90 !== undefined && m.peers_p10 !== undefined ? m.peers_p90 - m.peers_p10 : null)),
                    lineStyle: { opacity: 0 },
                    areaStyle: { color: tokens.muted, opacity: 0.12 },
                    stack: 'band',
                    symbol: 'none',
                    silent: true,
                },
            )
        }
        series.push({
            name: t('consolidated.summary_peers'),
            type: 'line',
            data: trend.months.map((m) => m.peers_p50 ?? null),
            lineStyle: { color: tokens.muted, type: 'dashed', width: 1.5 },
            itemStyle: { color: tokens.muted },
            symbol: 'none',
            endLabel: { show: true, formatter: t('consolidated.summary_peers'), color: tokens.muted, fontSize: 10 },
        })
        if (kpi.target.target !== undefined) {
            series.push({
                name: t('consolidated.target', { value: '' }).trim(),
                type: 'line',
                data: months.map(() => kpi.target.target),
                lineStyle: { color: tokens.excellent, type: 'dotted', width: 1.5 },
                itemStyle: { color: tokens.excellent },
                symbol: 'none',
                endLabel: { show: true, formatter: fmt(kpi.target.target), color: tokens.excellent, fontSize: 10 },
            })
        }
        series.push({
            name: t('consolidated.summary_pooled'),
            type: 'line',
            data: trend.months.map((m) => m.pooled ?? null),
            lineStyle: { color: tokens.accent, width: 2.5 },
            itemStyle: { color: tokens.accent },
            symbol: 'circle',
            symbolSize: 5,
            endLabel: { show: true, formatter: (p: { value: number }) => fmt(p.value), color: tokens.accent, fontSize: 11, fontWeight: 600 },
        })
        return {
            grid: { left: 48, right: 90, top: 16, bottom: 28 },
            xAxis: { type: 'category', data: months, axisLine: { lineStyle: { color: tokens.border } }, axisLabel: { color: tokens.muted, fontSize: 10 } },
            yAxis: { type: 'value', axisLabel: { color: tokens.muted, fontSize: 10, formatter: fmt }, splitLine: { lineStyle: { color: tokens.border } }, scale: true },
            tooltip: {
                trigger: 'axis',
                valueFormatter: (v: number | null) => (v === null || v === undefined ? '—' : fmt(v)),
            },
            series,
        }
    }, [trend, kpi, months, tokens, t, fmt])
    return <ReactECharts option={option} style={{ height: 260 }} notMerge opts={{ renderer: 'svg' }} />
}

/** Bars from the farthest to the closest to the target, tinted by tier, n under each. */
export function OutOfTargetBars({ rows, kpi }: { rows: GroupMatrixRow[]; kpi: GroupKPIMeta }) {
    const { locale } = useLocale()
    const lang = langOf(locale)
    const tokens = useChartTokens()
    const fmt = axisFormatter(kpi.unit, lang, locale)
    const target = kpi.target.target
    const items = rows
        .map((r) => ({ row: r, cell: r.values[kpi.code] }))
        .filter((x): x is { row: GroupMatrixRow; cell: GroupCell } => !!x.cell && !x.cell.suppressed && x.cell.value !== null && x.cell.value !== undefined && !!x.cell.out_of_target)
        .sort((a, b) => {
            const da = target === undefined ? 0 : Math.abs((a.cell.value as number) - target)
            const db = target === undefined ? 0 : Math.abs((b.cell.value as number) - target)
            return db - da
        })
    const option = useMemo(
        () => ({
            grid: { left: 48, right: 16, top: 16, bottom: 48 },
            xAxis: {
                type: 'category',
                data: items.map((x) => (x.row.perimeter.anonymous ? '…' : x.row.perimeter.name || x.row.perimeter.code || '')),
                axisLabel: { color: tokens.muted, fontSize: 10, interval: 0, rotate: items.length > 6 ? 30 : 0 },
                axisLine: { lineStyle: { color: tokens.border } },
            },
            yAxis: { type: 'value', axisLabel: { color: tokens.muted, fontSize: 10, formatter: fmt }, splitLine: { lineStyle: { color: tokens.border } } },
            tooltip: { trigger: 'item', formatter: (p: { dataIndex: number }) => { const x = items[p.dataIndex]; return `${x.row.perimeter.name ?? '…'}: ${fmt(x.cell.value as number)} · n = ${x.cell.n}` } },
            series: [
                {
                    type: 'bar',
                    data: items.map((x) => ({ value: x.cell.value, itemStyle: { color: tierColor(tierOf(x.cell), tokens) } })),
                    barMaxWidth: 36,
                    label: { show: true, position: 'insideBottom', formatter: (p: { dataIndex: number }) => `n=${items[p.dataIndex].cell.n}`, color: '#fff', fontSize: 10 },
                    markLine: target === undefined ? undefined : { silent: true, symbol: 'none', lineStyle: { type: 'dotted', color: tokens.excellent }, data: [{ yAxis: target }] },
                },
            ],
        }),
        [items, tokens, fmt, target],
    )
    return <ReactECharts option={option} style={{ height: 240 }} notMerge opts={{ renderer: 'svg' }} />
}

/** Value on x, volume (n) on y; the zone beyond the target tinted; the median of volume dotted. */
export function VolumeValueScatter({ rows, kpi }: { rows: GroupMatrixRow[]; kpi: GroupKPIMeta }) {
    const { locale } = useLocale()
    const lang = langOf(locale)
    const tokens = useChartTokens()
    const fmt = axisFormatter(kpi.unit, lang, locale)
    const pts = rows
        .map((r) => ({ row: r, cell: r.values[kpi.code] }))
        .filter((x): x is { row: GroupMatrixRow; cell: GroupCell } => !!x.cell && !x.cell.suppressed && x.cell.value !== null && x.cell.value !== undefined)
    const volumes = pts.map((x) => x.cell.n).sort((a, b) => a - b)
    const medianVolume = volumes.length ? volumes[Math.floor(volumes.length / 2)] : undefined
    const target = kpi.target.target
    const option = useMemo(
        () => ({
            grid: { left: 48, right: 16, top: 16, bottom: 32 },
            xAxis: { type: 'value', name: text(kpi.label, lang), nameTextStyle: { color: tokens.muted, fontSize: 10 }, axisLabel: { color: tokens.muted, fontSize: 10, formatter: fmt }, splitLine: { lineStyle: { color: tokens.border } }, scale: true },
            yAxis: { type: 'value', name: 'n', nameTextStyle: { color: tokens.muted, fontSize: 10 }, axisLabel: { color: tokens.muted, fontSize: 10 }, splitLine: { lineStyle: { color: tokens.border } } },
            tooltip: { trigger: 'item', formatter: (p: { dataIndex: number }) => { const x = pts[p.dataIndex]; return `${x.row.perimeter.name ?? '…'}: ${fmt(x.cell.value as number)} · n = ${x.cell.n}` } },
            series: [
                {
                    type: 'scatter',
                    data: pts.map((x) => ({ value: [x.cell.value, x.cell.n], itemStyle: { color: tierColor(tierOf(x.cell), tokens) } })),
                    symbolSize: 12,
                    markLine: { silent: true, symbol: 'none', data: [
                        ...(target === undefined ? [] : [{ xAxis: target, lineStyle: { type: 'dotted', color: tokens.excellent } }]),
                        ...(medianVolume === undefined ? [] : [{ yAxis: medianVolume, lineStyle: { type: 'dotted', color: tokens.muted } }]),
                    ] },
                    markArea: target === undefined ? undefined : {
                        silent: true,
                        itemStyle: { color: tokens.watch, opacity: 0.06 },
                        data: [kpi.direction === 'higher_better' ? [{ xAxis: 'min' }, { xAxis: target }] : [{ xAxis: target }, { xAxis: 'max' }]],
                    },
                },
            ],
        }),
        [pts, tokens, fmt, target, medianVolume, kpi, lang],
    )
    return <ReactECharts option={option} style={{ height: 260 }} notMerge opts={{ renderer: 'svg' }} />
}

/**
 * Which perimeters carry half of the volume in deviation — n × the gap to
 * the peers' median in the bad direction. Says where a gesture pays, never
 * who works least well.
 */
export function ConcentrationList({ rows, kpi, peersP50 }: { rows: GroupMatrixRow[]; kpi: GroupKPIMeta; peersP50: number | undefined }) {
    const { locale } = useLocale()
    const lang = langOf(locale)
    const { t } = useTranslation('group-steering')
    if (peersP50 === undefined) return <p className="text-sm text-muted-foreground">{t('consolidated.peers_band_hidden', { count: 5 })}</p>
    const sign = kpi.direction === 'higher_better' ? -1 : 1
    const items = rows
        .map((r) => ({ row: r, cell: r.values[kpi.code] }))
        .filter((x): x is { row: GroupMatrixRow; cell: GroupCell } => !!x.cell && !x.cell.suppressed && x.cell.value !== null && x.cell.value !== undefined)
        .map((x) => ({ ...x, weight: Math.max(0, sign * ((x.cell.value as number) - peersP50)) * x.cell.n }))
        .filter((x) => x.weight > 0)
        .sort((a, b) => b.weight - a.weight)
    const total = items.reduce((s, x) => s + x.weight, 0)
    if (total === 0) return <p className="text-sm text-muted-foreground">{t('consolidated.no_out_of_target')}</p>
    let cumulative = 0
    return (
        <ul className="space-y-1.5" data-testid="concentration-list">
            {items.map((x) => {
                cumulative += x.weight
                const share = x.weight / total
                const half = cumulative - x.weight < total / 2
                return (
                    <li key={x.row.perimeter.uid ?? x.row.perimeter.name} className="flex items-center gap-3 text-sm">
                        <span className={`w-40 truncate ${half ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{x.row.perimeter.anonymous ? '…' : x.row.perimeter.name}</span>
                        <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                            <div className="h-full" style={{ width: `${share * 100}%`, background: `var(${half ? TIER_VAR.watch : TIER_VAR.none})` }} />
                        </div>
                        <span className="w-14 text-right tabular-nums text-muted-foreground">{Math.round(share * 100)} %</span>
                        <span className="w-24 text-right tabular-nums text-muted-foreground">{formatUnit(x.cell.value, kpi.unit, lang, locale)} · n={x.cell.n}</span>
                    </li>
                )
            })}
        </ul>
    )
}
